import { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import LoadingTab from '../../AccountSettingsTabs/LoadingTab';
import CompanyProfileForm from './CompanyProfileForm/CompanyProfileForm';
import CompanyUploadImageSection from './CompanyUploadImageSection';
import { EditCompanyProfileForm, useCompanyProfileForm } from './useCompanyProfileForm';
import { CompanyImage } from '@/Clients/CompanyClient';
import { useCompanyContext } from '@/Context/CompanyContext';
import { useNotificationContext } from '@/Context/NotificationContext';
import { CompanyProfile } from '@/Models/CompanyProfile';
import SlugExistsError from '@/Utils/HTTP/Errors/SlugExistsError';
import UIStrings from '@/Utils/UIStrings';

const CompanyProfileTab = () => {
    const { currentCompanyProfile, updateCompanyProfile } = useCompanyContext();
    const [savingInProgress, setSavingInProgress] = useState<boolean>(false);
    const { showSuccessToaster, showFailToaster } = useNotificationContext();
    const { fetchError, ...form } = useCompanyProfileForm();

    const submitHandler = async (values: EditCompanyProfileForm) => {
        try {
            setSavingInProgress(true);

            await updateCompanyProfile({
                ...currentCompanyProfile,
                ...values,
            } as CompanyProfile);
            showSuccessToaster(UIStrings.AccountDataSuccessfullyUpdated);
        } catch (error) {
            if (error instanceof SlugExistsError) {
                const { detail } = error;
                form.setError('companySlug', { type: 'custom', message: detail });
            }
            showFailToaster();
        } finally {
            setSavingInProgress(false);
        }
    };

    return (
        <LoadingTab isLoading={form.formState.isLoading} loadingFailed={!!fetchError}>
            <FormProvider {...form}>
                <form encType="multipart/form-data" onSubmit={form.handleSubmit(submitHandler)}>
                    <CompanyProfileForm loading={savingInProgress}>
                        <CompanyUploadImageSection
                            circularCrop
                            title={UIStrings.Icon}
                            description={UIStrings.AScaledDownVersionOfYourLogo}
                            src={currentCompanyProfile.companyIconURL}
                            companyImageType={CompanyImage.Icon}
                        />
                        <CompanyUploadImageSection
                            title={UIStrings.CompanyLogo}
                            description={UIStrings.TheFullScaleVersionOfYourLogo}
                            src={currentCompanyProfile.companyLogoURL}
                            aspect={3}
                            companyImageType={CompanyImage.Logo}
                            className="h-18 w-52 rounded-xl"
                        />
                    </CompanyProfileForm>
                </form>
            </FormProvider>
        </LoadingTab>
    );
};

export default CompanyProfileTab;
